import { useEventListener, useUnmountEffect } from 'primereact/hooks'
import { classNames } from 'primereact/utils'
import React, { useEffect, useRef } from 'react'
import { LoadingOverlay } from 'shared/loading-overlay/loading-overlay'
import { AppTopbarRef } from 'types'
import AppSidebar from './components/app-sidebar'
import AppTopbar from './components/app-topbar'
import { useLayout } from './context/layout-context'
import { useLoading } from './context/loading-context'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout = ({ children }: LayoutProps) => {
  const { layoutConfig, layoutState, setLayoutState } = useLayout()

  const topbarRef = useRef<AppTopbarRef>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const { isLoading } = useLoading()

  // For now, isNavigating is false - can be enhanced later
  const isNavigating = false

  const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] =
    useEventListener({
      type: 'click',
      listener: (event) => {
        const isOutsideClicked = !(
          sidebarRef.current?.isSameNode(event.target as Node) ||
          sidebarRef.current?.contains(event.target as Node) ||
          topbarRef.current?.menubutton?.isSameNode(event.target as Node) ||
          topbarRef.current?.menubutton?.contains(event.target as Node)
        )

        if (isOutsideClicked) {
          hideMenu()
        }
      },
    })

  const [
    bindProfileMenuOutsideClickListener,
    unbindProfileMenuOutsideClickListener,
  ] = useEventListener({
    type: 'click',
    listener: (event) => {
      const isOutsideClicked = !(
        topbarRef.current?.topbarmenu?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenu?.contains(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.contains(event.target as Node)
      )

      if (isOutsideClicked) {
        hideProfileMenu()
      }
    },
  })

  const hideMenu = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }))
    unbindMenuOutsideClickListener()
    unblockBodyScroll()
  }

  const hideProfileMenu = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      profileSidebarVisible: false,
    }))
    unbindProfileMenuOutsideClickListener()
  }

  const blockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll')
    } else {
      document.body.className += ' blocked-scroll'
    }
  }

  const unblockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll')
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      )
    }
  }

  useEffect(() => {
    if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive) {
      bindMenuOutsideClickListener()
    }

    layoutState.staticMenuMobileActive && blockBodyScroll()
  }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive])

  useEffect(() => {
    if (layoutState.profileSidebarVisible) {
      bindProfileMenuOutsideClickListener()
    }
  }, [layoutState.profileSidebarVisible])

  useUnmountEffect(() => {
    unbindMenuOutsideClickListener()
    unbindProfileMenuOutsideClickListener()
  })

  const containerClass = classNames('layout-wrapper', {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive':
      layoutState.staticMenuDesktopInactive &&
      layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive,
    'p-input-filled': layoutConfig.inputStyle === 'filled',
    'p-ripple-disabled': !layoutConfig.ripple,
  })

  return (
    <React.Fragment>
      <LoadingOverlay isVisible={isNavigating || isLoading} />
      <div className={containerClass}>
        <div
          ref={sidebarRef}
          className='layout-sidebar'
        >
          <AppSidebar />
        </div>
        <div className='layout-main-container content-layout'>
          <AppTopbar ref={topbarRef} />
          <div className='layout-main'>{children}</div>
        </div>
      </div>
    </React.Fragment>
  )
}
